import React, {ReactNode, useEffect, useState} from "react";
import {SetStateFn} from "@/utils/SetStateUtils";
import {useRouteLoaderData} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import useAuth from "../authentication/use-auth";
import {LoadedData} from "../authentication/user-loader";
import {DashboardStatus} from "@/data/entities/dashboard";
import {RestaurantLocation} from "@/data/entities/restaurant";
import {api} from "@/data/api";

interface RestaurantContextInterface {
    dashboardStatus: DashboardStatus,
    locations: RestaurantLocation[],
    setLocations: SetStateFn<RestaurantLocation[]>,
    dashboardLocation: RestaurantLocation,
    setDashboardLocation: SetStateFn<RestaurantLocation>,
}

const AuthContext = React.createContext<RestaurantContextInterface>({} as RestaurantContextInterface);

export const useRestaurant = () => {
    return React.useContext<RestaurantContextInterface>(AuthContext);
};

type Props = {
    children: ReactNode,
}

const RestaurantProvider = ({children}: Props) => {
    const data: LoadedData = useRouteLoaderData("auth-root") as LoadedData;
    const {user, setUser} = useAuth();
    const [locations, setLocations] = useState<RestaurantLocation[]>(data.restaurantLocations);
    const [dashboardLocation, setDashboardLocation] = useState<RestaurantLocation>(
        data
            .restaurantLocations
            .filter(l => l.id === user.preferredRestaurantLocation.id)[0]);
    const {data: dashboardStatus} = useQuery({
        queryKey: ["dashboard-status", dashboardLocation.id],
        queryFn: async () => api.dashboard.getDashboardStatus(dashboardLocation.id),
        initialData: {
            openJobApplicationsCount: 0,
        },
        refetchInterval: 300_000, // Every 5 minutes
    });

    // Update user location preference after change
    useEffect(() => {
        if (dashboardLocation.id === user?.preferredRestaurantLocation.id) {
            return;
        }

        // TODO - Tanstack
        api.user.updatePreferredRestaurantLocation(dashboardLocation.id)
            .then(updatedAdmin => setUser(updatedAdmin))
            .catch(() => console.error("Error updating preferred restaurant location for admin!"))
    }, [dashboardLocation, user, setUser]);

    return (
        <AuthContext.Provider
            value={{
                locations,
                setLocations,
                dashboardLocation,
                setDashboardLocation,
                dashboardStatus,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default RestaurantProvider;